import { useState, useCallback, useMemo, useEffect } from 'react';
import { getGameMetadata, getStatusLabel, SocketEvent, getLifecycle, isTerminal as isGameTerminal } from '@swtorbit/shared-socket-events';
import { getEnabledPlayers, useSocketContext } from '@swtorbit/shared-react-hooks';
import { useGameContext } from '../contexts/GameContext';
import { formatDate } from '../utils/formatDate';
import { RegistrationsList } from './RegistrationsList';

export function GameSettings() {
  const { selectedGame, toggleClientDebug } = useGameContext();
  const { socket } = useSocketContext();
  const [preRegRoleId, setPreRegRoleId] = useState<string>('');
  const [roundEndingDurationInput, setRoundEndingDurationInput] = useState<string>('');

  // Filter out disabled players from the selected game
  const enabledPlayers = useMemo(() => getEnabledPlayers(selectedGame), [selectedGame?.players]);

  const lifecycleDefaultDurationMs = useMemo(
    () => (selectedGame ? getLifecycle(selectedGame.gameType).roundEndingDurationMs : 0),
    [selectedGame?.gameType],
  );

  useEffect(() => {
    setPreRegRoleId(selectedGame?.preRegistrationRoleId ?? '');
  }, [selectedGame?.dbId, selectedGame?.preRegistrationRoleId]);

  useEffect(() => {
    setRoundEndingDurationInput(
      selectedGame?.roundEndingDurationMs != null ? String(selectedGame.roundEndingDurationMs) : '',
    );
  }, [selectedGame?.dbId, selectedGame?.roundEndingDurationMs]);

  const handleToggleClientDebug = useCallback(() => {
    if (!selectedGame) return;
    toggleClientDebug({
      roomCode: selectedGame.roomCode,
      show: !selectedGame.showClientDebug,
    });
  }, [selectedGame, toggleClientDebug]);

  const handlePreRegRoleBlur = useCallback(() => {
    if (!selectedGame || !socket) return;
    const trimmed = preRegRoleId.trim();
    if (trimmed === (selectedGame.preRegistrationRoleId ?? '')) return;
    socket.emit(SocketEvent.ADMIN_UPDATE_GAME_STATUS, {
      roomCode: selectedGame.roomCode,
      status: selectedGame.status,
      preRegistrationRoleId: trimmed || null,
    });
  }, [selectedGame, socket, preRegRoleId]);

  const handleRoundEndingDurationBlur = useCallback(() => {
    if (!selectedGame || !socket) return;
    const trimmed = roundEndingDurationInput.trim();
    let next: number | null;
    if (trimmed === '') {
      next = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        setRoundEndingDurationInput(
          selectedGame.roundEndingDurationMs != null ? String(selectedGame.roundEndingDurationMs) : '',
        );
        return;
      }
      next = parsed;
    }
    if (next === (selectedGame.roundEndingDurationMs ?? null)) return;
    socket.emit(SocketEvent.ADMIN_UPDATE_ROUND_ENDING_DURATION, {
      roomCode: selectedGame.roomCode,
      durationMs: next,
    });
  }, [selectedGame, socket, roundEndingDurationInput]);

  if (!selectedGame) {
    return (
      <div className="p-lg">
        <div className="text-center p-2xl text-slate">
          <h2>Game Settings</h2>
          <p>Select a game from the dropdown above to view and manage its settings.</p>
        </div>
      </div>
    );
  }

  const gameMetadata = getGameMetadata(selectedGame.gameType);
  const statusInfo = getStatusLabel(selectedGame.status);
  const status = selectedGame.status;
  const isTerminal = isGameTerminal(selectedGame.gameType, status);
  const showRegistrations = status === 'pre_registration' || status === 'pool_creation' || status === 'team_creation' || status === 'lobby' || status === 'active' || status === 'round_ending';

  // Fallback if game metadata not found
  const gameIcon = gameMetadata?.icon || '🎮';

  return (
    <div className="p-lg">
      <h2 className="text-white">Game Settings</h2>

      {/* Game Info Card */}
      <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base glow glow-blue-light p-lg mb-lg">
        <div className="d-flex justify-content-between align-items-center mb-sm pb-sm border-bottom">
          <div className="d-flex align-items-center gap-xs">
            <span>{gameIcon}</span>
            <h3>{selectedGame.name || 'Unnamed Game'}</h3>
          </div>
          <div className={`d-flex align-items-center gap-xs ${statusInfo.className}`}>
            <span>{statusInfo.icon}</span>
            <span>{statusInfo.label}</span>
          </div>
        </div>

        <div className="d-flex flex-column gap-xs">
          <div className="d-flex justify-content-between align-items-center">
            <span >Room Code:</span>
            <span className="badge badge--lg">{selectedGame.roomCode}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span >Game Type:</span>
            <span>{gameMetadata.title}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span >Created:</span>
            <span>{formatDate(selectedGame.createdAt, { includeWeekday: true })}</span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span >Players:</span>
            <span>
              {enabledPlayers.length}
              {enabledPlayers.length !== selectedGame.players.length &&
                ` (${selectedGame.players.length - enabledPlayers.length} disabled)`}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <span >Rounds:</span>
            <span>{selectedGame.rounds.length}</span>
          </div>
        </div>
      </div>

      {/* Settings Controls - Client Debug Mode */}
      <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base glow glow-blue-light p-lg mb-lg">
        <h3>Game Controls</h3>

        <div className="field field--row">
          <div className="">
            <span className="field__label">Client Debug Tab</span>
            <span className="field__hint">
              Show debug tab on connected player clients (logs are always captured)
            </span>
          </div>
          <label className="check">
            <input
              className="switch"
              type="checkbox"
              checked={selectedGame.showClientDebug ?? false}
              onChange={handleToggleClientDebug}
            />
          </label>
        </div>

        {selectedGame.debugEnabled && (
          <div className="field field--row">
            <div className="">
              <span className="field__label">Verbose Logging</span>
              <span className="field__hint">
                DEBUG/INFO level logs are being captured (set at game creation)
              </span>
            </div>
            <span className="text-green-dark">✓ Enabled</span>
          </div>
        )}
      </div>

      {/* Registration Settings */}
      {!isTerminal && (
        <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base glow glow-blue-light p-lg mb-lg">
          <h3>Registration Settings</h3>
          <div className="field field--row">
            <div className="">
              <span className="field__label">Required Discord Role ID (optional)</span>
              <span className="field__hint">
                If set, only Discord members with this role can run <code>/registration</code>. Leave empty to require the &quot;Manage Channels&quot; permission.
              </span>
            </div>
            <input
              type="text"
              value={preRegRoleId}
              onChange={(e) => setPreRegRoleId(e.target.value)}
              onBlur={handlePreRegRoleBlur}
              placeholder="e.g. 123456789012345678"
              className="input"
            />
          </div>
          <div className="field field--row">
            <div className="">
              <span className="field__label">Round Ending Duration (ms)</span>
              <span className="field__hint">
                How long the end-of-round scoresheet animation plays before the game auto-advances back to the lobby. Leave empty to use the default ({lifecycleDefaultDurationMs}ms). Admins can also skip the wait manually.
              </span>
            </div>
            <input
              type="number"
              min={0}
              step={100}
              value={roundEndingDurationInput}
              onChange={(e) => setRoundEndingDurationInput(e.target.value)}
              onBlur={handleRoundEndingDurationBlur}
              placeholder={String(lifecycleDefaultDurationMs)}
              className="input"
            />
          </div>
        </div>
      )}

      {showRegistrations && (
        <RegistrationsList
          gameId={selectedGame.dbId}
          roomCode={selectedGame.roomCode}
          gameType={selectedGame.gameType}
          archived={selectedGame.archived}
          guildId={selectedGame.discord?.guildId}
        />
      )}

      {/* Completed Game Info */}
      {isTerminal && selectedGame.endedAt && (
        <div className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base glow glow-blue-light p-lg mb-lg">
          <h3>Game {status === 'completed' ? 'Completed' : 'Abandoned'}</h3>
          <p>This game ended on {formatDate(selectedGame.endedAt, { includeWeekday: true })}</p>
          {selectedGame.endReason === 'inactive' && (
            <p>It was abandoned automatically after 30 days without activity.</p>
          )}
        </div>
      )}
    </div>
  );
}
